/* eslint-disable prettier/prettier */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  addUpdateCampaignRuleUrl,
  fetchCampaigns,
  updateCampaignUrl,
  fetchStatesUrl,
  fetchDivisionsUrl,
  fetchProvincesUrl,
} from 'src/services/urls'

const initialState = {
  loading: false,
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  error: '',
  pageSize: 10,
  activePage: 0,
  mode: '',
  provinceValues: [],
  divisionValues: [],
  stateValues: [],
  initialization: {
    allCampaignTypes: {},
    stateRuleExist: false,
    states: [],
    country: {},
    languages: {},
    data: {},
    brandList: [],
    productList: [],
  },
  rule: {
    _id: '',
    campaignId: '',
    campaignType: '',
    name: '',
    states: [],
    startTimestamp: '',
    endTimestamp: '',
    isActive: null,
    campaignRuleExist: null,
  },
}

export const fetchAllCampaigns = createAsyncThunk(
  'campaignManagement/fetchAllCampaigns',
  async (data, { dispatch, getState }) => {
    try {
      let query = '?'
      for (let key in data) {
        query += `${key}=${data[key]}&`
      }

      query = query.slice(0, -1)
      const url = fetchCampaigns(query)
      const response = await makeRequest(url)
      return { ...response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const updateCampaignRequest = createAsyncThunk(
  'campaignManagement/updateCampaignRequest',
  async (data, { dispatch, getState }) => {
    try {
      const url = updateCampaignUrl() + '/' + data.id
      const response = await makeRequest(url, 'PATCH', data.value)
      if (response && response.data && response.data.statusCode === 200) {
        dispatch(
          showToast({
            title: 'Updated',
            message: `Campaign Updated Successfully`,
            color: 'success',
          }),
        )
      }

      return { ...response.data.data, id: data.id }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message, id: data.id }
    }
  },
)

export const addUpdateCampaignRule = createAsyncThunk(
  'campaignManagement/addUpdateCampaignRule',
  async (data, { dispatch, getState }) => {
    try {
      const url = addUpdateCampaignRuleUrl()
      const response = await makeRequest(url, 'POST', data)
      if (response && response.data && response.data.statusCode === 200) {
        dispatch(
          showToast({
            title: 'Updated',
            message: `Campaign Rule Updated Successfully`,
            color: 'success',
          }),
        )
      }

      return { ...response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchAllProvinces = createAsyncThunk(
  'campaignManagement/fetchProvinces',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchProvincesUrl(), 'GET')
      return response.data.data // Assuming the API response has a 'data' property with province information
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchAllDivisions = createAsyncThunk(
  'campaignManagement/fetchDivisions',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchDivisionsUrl(), 'GET')
      return response.data.data // Assuming the API response has a 'data' property with division information
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchAllStates = createAsyncThunk(
  'campaignManagement/fetchStates',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchStatesUrl(), 'GET')
      return response.data.data // Assuming the API response has a 'data' property with state information
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

const campaignManagementSlice = createSlice({
  name: 'campaignManagement',
  initialState,
  reducers: {
    resetDocs: (state, action) => {
      state.docs = []
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setCreateUpdateCampaign: (state, action) => {
      state.mode = action.payload.mode
      state.initialization = action.payload.initialization
    },
    setActiveCampaign: (state, action) => {
      state.docs = state.docs.map((camp) => {
        if (camp._id === action.payload) {
          return { ...camp, isActive: !camp.isActive }
        } else {
          return camp
        }
      })
    },
    setCampaignRuleInitialize: (state, action) => {
      state.rule = action.payload
    },
    resetRule: (state, action) => {
      state.rule = {
        _id: '',
        campaignId: '',
        campaignType: '',
        name: '',
        states: [],
        startTimestamp: '',
        endTimestamp: '',
        isActive: null,
        campaignRuleExist: null,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllCampaigns.pending, (state, action) => {})
    builder.addCase(fetchAllCampaigns.fulfilled, (state, action) => {
      state.data = action.payload
      let trackDocs = new Map()
      state.docs.forEach((d) => {
        if (!trackDocs.get(d._id)) trackDocs.set(d._id, d._id)
      })
      let newDocs = []
      action.payload.docs.forEach((d) => (!trackDocs.get(d._id) ? newDocs.push(d) : false))
      state.docs = [...state.docs, ...newDocs]
      state.loading = false
    })
    builder.addCase(fetchAllCampaigns.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(updateCampaignRequest.pending, (state, action) => {})
    builder.addCase(updateCampaignRequest.fulfilled, (state, action) => {
      if ('error' in action.payload) {
        state.docs = state.docs.map((camp) => {
          if (camp._id === action.payload.id) {
            return { ...camp, isActive: !camp.isActive }
          } else {
            return camp
          }
        })
      }
    })
    builder.addCase(updateCampaignRequest.rejected, (state, action) => {
      state.docs = state.docs.map((camp) => {
        if (camp._id === action.payload.id) {
          return { ...camp, isActive: !camp.isActive }
        } else {
          return camp
        }
      })
      state.error = action.error.message
    })

    builder.addCase(fetchAllProvinces.fulfilled, (state, action) => {
      state.provinceValues = action.payload
      state.loading = false
    })
    builder.addCase(fetchAllProvinces.rejected, (state, action) => {
      state.loading = false
      state.provinceValues = {}
      state.error = action.error.message
    })

    builder.addCase(fetchAllDivisions.fulfilled, (state, action) => {
      state.divisionValues = action.payload
      state.loading = false
    })
    builder.addCase(fetchAllDivisions.rejected, (state, action) => {
      state.loading = false
      state.divisionValues = {}
      state.error = action.error.message
    })

    builder.addCase(fetchAllStates.fulfilled, (state, action) => {
      state.stateValues = action.payload
      state.loading = false
    })

    builder.addCase(fetchAllStates.rejected, (state, action) => {
      state.loading = false
      state.stateValues = {}
      state.error = action.error.message
    })

    builder.addCase(addUpdateCampaignRule.pending, (state, action) => {})
    builder.addCase(addUpdateCampaignRule.fulfilled, (state, action) => {})
    builder.addCase(addUpdateCampaignRule.rejected, (state, action) => {
      state.error = action.error.message
    })
  },
})
export const {
  resetDocs,
  setLoading,
  setActivePage,
  setPageSize,
  setCreateUpdateCampaign,
  setActiveCampaign,
  setCampaignRuleInitialize,
  resetRule,
} = campaignManagementSlice.actions
export default campaignManagementSlice.reducer
